import { ActionIcon, Button, Card, Grid, Group, Stack, Text } from '@mantine/core';
import { IconEye, IconLayoutGrid, IconList, IconPlus, IconTrash } from '@tabler/icons-react';
import type { QueryObserverResult } from '@tanstack/react-query';
import { useContextMenu } from 'mantine-contextmenu';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Show } from '../../../../../../react-commons/components/control-flow/Show';
import type { SerializableRenderTreeNode } from '../../../../../../react-commons/components/data-display/tree/RenderTreeNode';
import { Tree } from '../../../../../../react-commons/components/data-display/tree/Tree';
import { TreeHoverButton } from '../../../../../../react-commons/components/inputs/buttons/TreeHoverButton';
import { OpenModal } from '../../../../../../react-commons/utils/modals/openModal';
import { setStandardGridView } from '../../../../persisted/global/globalActions';
import { ResourceType } from '../../../../resource/enumerations/ResourceType';
import {
    defaultStandardResource,
    type StandardResource,
    useStandardActions,
} from '../../../../resource/resources/artifacts/standard/standardResource';
import { useUserActions } from '../../../../resource/resources/persistables/user/userResource';
import type { RootState } from '../../../../store/rootReducer';
import { useUserId } from '../../../authentication/useUserId';
import { useDeleteResourceDialogFunction } from '../../../reusable-components/dialogs/UseDeleteResourceDialogFunction';
import { ResourceValidationTitleBar } from '../../../reusable-components/validation/ResourceValidationTitleBar';
import { defaultLoadingNode } from '../../../util/parseTreeFunctions';

type StandardsStructureComponentTreeNode = SerializableRenderTreeNode<StandardResource>;

const parseTree = (
    resources: QueryObserverResult<Array<StandardResource> | null>
): Array<StandardsStructureComponentTreeNode> => {
    if (resources.isLoading) {
        return [defaultLoadingNode];
    }
    if (!resources.data) {
        return [];
    }

    return resources.data
        .map((resource) => ({
            key: resource.id,
            label: resource.name,
            data: resource,
        }))
        .toSorted((a, b) => a.label.localeCompare(b.label));
};

export const StandardsStructureComponent = ({
    projectVersionId,
    onSelected,
}: {
    projectVersionId: string | undefined;
    onSelected: (id: string) => void;
}) => {
    const { showContextMenu } = useContextMenu();
    const { id: projectId } = useParams<{ id: string }>();
    const userId = useUserId('');
    const deleteResourceDialogFunction = useDeleteResourceDialogFunction();

    const dispatch = useDispatch();
    const viewMode = useSelector((state: RootState) => (state.persisted.global.standardGridView ? 'grid' : 'list'));
    const [selectedNode, setSelectedNode] = React.useState<StandardsStructureComponentTreeNode | null>(null);
    const [nodes, setNodes] = React.useState<Array<StandardsStructureComponentTreeNode>>([]);

    const standardActions = useStandardActions();
    const standardsQuery = standardActions.useGetAllResources(projectVersionId);
    const userActions = useUserActions();
    const userSystemRights = userActions.useGetUserEffectiveRightsOfUser(userId, projectId);
    const addStandard = standardActions.useAddResource();
    const deleteStandard = standardActions.useDeleteResource();

    const isProcessAdmin = userSystemRights.data?.isProcessAdmin;

    React.useEffect(() => {
        const newNodes = parseTree(standardsQuery);
        setNodes(newNodes);
    }, [standardsQuery.data, projectVersionId]);

    React.useEffect(() => {
        setSelectedNode(null);
    }, [projectVersionId]);

    const nodeSelected = (node: StandardsStructureComponentTreeNode) => {
        setSelectedNode(node);
        onSelected(node.data.objectId);
    };

    const deleteResource = (id: string, name: string) =>
        deleteResourceDialogFunction.deleteResource(id, name, ResourceType.STANDARD, deleteStandard);

    return (
        <Card shadow={'sm'} padding={'md'} radius={'md'} withBorder style={{ height: '100%' }}>
            <Stack h={'100%'}>
                <ResourceValidationTitleBar
                    resourceType={ResourceType.STANDARD}
                    customButtons={
                        <Group>
                            <ActionIcon
                                size={'sm'}
                                onClick={(event) => {
                                    showContextMenu([
                                        {
                                            key: 'list',
                                            title: 'List',
                                            icon: <IconList />,
                                            onClick: () => dispatch(setStandardGridView(false)),
                                        },
                                        {
                                            key: 'grid',
                                            title: 'Grid',
                                            icon: <IconLayoutGrid />,
                                            onClick: () => dispatch(setStandardGridView(true)),
                                        },
                                    ])(event);
                                }}
                                variant={'transparent'}
                                color={'var(--mantine-color-base-button)'}
                            >
                                <IconEye />
                            </ActionIcon>
                            <Show when={isProcessAdmin}>
                                <ActionIcon
                                    size={'sm'}
                                    onClick={() =>
                                        OpenModal.StringInputModal(
                                            'Create a new Standard',
                                            (value, multiLineValue) => {
                                                addStandard.mutate({
                                                    resource: {
                                                        ...defaultStandardResource,
                                                        name: value,
                                                        description: multiLineValue ?? '',
                                                    },
                                                    parentId: projectVersionId,
                                                });
                                            },
                                            undefined,
                                            'Name',
                                            true,
                                            'Description',
                                            800
                                        )
                                    }
                                    variant={'transparent'}
                                    color={'var(--mantine-color-base-button)'}
                                >
                                    <IconPlus />
                                </ActionIcon>
                            </Show>
                        </Group>
                    }
                >
                    <Text>Standards ({standardsQuery.data?.length ?? 0})</Text>
                </ResourceValidationTitleBar>
                <Show when={viewMode === 'list'}>
                    <Tree
                        nodes={nodes}
                        rootExpanded
                        selectedNodeKey={selectedNode?.key}
                        onNodeSelected={nodeSelected}
                        getElements={(node) =>
                            isProcessAdmin ?
                                {
                                    hoverNode: (
                                        <TreeHoverButton
                                            Icon={IconTrash}
                                            onClick={() => deleteResource(node.data.id, node.data.name)}
                                        />
                                    ),
                                }
                            :   {}
                        }
                    />
                </Show>
                <Show when={viewMode === 'grid'}>
                    <Grid>
                        {nodes.map((node) => (
                            <Grid.Col key={node.key} span={{ base: 12, md: 6 }}>
                                <Button
                                    w={'100%'}
                                    size={'lg'}
                                    rightSection={
                                        isProcessAdmin && (
                                            <TreeHoverButton
                                                Icon={IconTrash}
                                                onClick={() => deleteResource(node.data.id, node.data.name)}
                                            />
                                        )
                                    }
                                    justify="space-between"
                                    style={
                                        selectedNode?.key === node.key ?
                                            { border: '2px solid var(--mantine-text-secondary-color)' }
                                        :   undefined
                                    }
                                    onClick={() => {
                                        nodeSelected(node);
                                    }}
                                >
                                    {node.label}
                                </Button>
                            </Grid.Col>
                        ))}
                    </Grid>
                </Show>
            </Stack>
        </Card>
    );
};
